import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Menu, Search, HelpCircle, Sun, Moon, SunMoon } from 'lucide-react';
import { NotificationBell } from '@/shared/components/NotificationBell';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { Button } from '@/shared/components/ui/button';
import { useUIStore } from '@/shared/stores/uiStore';
import { useAuth } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/shared/config/routes';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toggleMobileSidebar = useUIStore((s) => s.toggleMobileSidebar);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const { user, signOut } = useAuth();

  return (
    <header className="relative z-40 flex h-14 shrink-0 items-center gap-4 border-b border-border bg-background px-4">
      {/* Mobile sidebar toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleMobileSidebar}
        className="lg:hidden"
        aria-label={t('actions.toggle_menu')}
      >
        <Menu className="h-5 w-5" strokeWidth={1.5} />
      </Button>

      {/* Logo on mobile */}
      <div className="lg:hidden">
        <img
          src="/logo-horizontal.svg"
          alt={t('app.name')}
          className="h-6 dark:hidden"
        />
        <img
          src="/logo-horizontal-white.svg"
          alt={t('app.name')}
          className="hidden h-6 dark:block"
        />
      </div>

      {/* Spacer to align search and actions to the right */}
      <div className="flex-1" />

      {/* Search trigger — compact on md, full on lg+ */}
      <button
        type="button"
        className="hidden items-center gap-2 rounded-md border border-border bg-card shadow-xs px-3 py-2 text-sm text-muted-foreground transition-all hover:border-primary/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:flex md:w-48 lg:w-96"
        aria-label={t('actions.search')}
      >
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="flex-1 text-left">{t('header.search_placeholder')}</span>
        <kbd className="hidden rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:inline">
          Ctrl+K
        </kbd>
      </button>

      {/* Mobile search icon */}
      <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('actions.search')}>
        <Search className="h-5 w-5" strokeWidth={1.5} />
      </Button>

      {/* Notifications */}
      <NotificationBell />

      {/* Help */}
      <Button
        variant="ghost"
        size="icon"
        className="hidden md:flex"
        aria-label={t('header.help')}
        onClick={() => window.open('https://docs.flowqms.net', '_blank', 'noopener,noreferrer')}
      >
        <HelpCircle className="h-5 w-5" strokeWidth={1.5} />
      </Button>

      {/* Theme toggle */}
      <ThemeToggle />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={user?.avatar_url ?? undefined}
                alt={user?.full_name ?? ''}
              />
              <AvatarFallback className="text-xs">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium text-foreground">
              {user?.full_name ?? t('user_menu.guest')}
            </p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { void navigate(ROUTES.SETTINGS.GENERAL); }}>
            {t('user_menu.profile')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { void navigate(ROUTES.SETTINGS.ROOT); }}>
            {t('user_menu.settings')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            {t('theme.title')}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1 px-2 pb-1.5">
            {(['light', 'dark', 'system'] as const).map((opt) => {
              const Icon = opt === 'light' ? Sun : opt === 'dark' ? Moon : SunMoon;
              return (
                <Button
                  key={opt}
                  variant={theme === opt ? 'default' : 'ghost'}
                  size="icon"
                  className="h-8 w-8"
                  title={t(`theme.${opt}`)}
                  onClick={() => { setTheme(opt); }}
                >
                  <Icon className="h-3.5 w-3.5" />
                </Button>
              );
            })}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { void signOut(); }}>
            {t('user_menu.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
